import React, { useState, useRef, useEffect } from 'react';
import { DmnModel } from '../types';
import { DecisionTable } from './organisms/DecisionTable';
import { Button } from './atoms/BaseComponents';
import { TestModal } from './organisms/TestModal';
import { INITIAL_DMN_XML, parseDmnXml, generateDmnXml } from '../utils/dmnXml';
import { 
  Upload, 
  Download, 
  Save, 
  Play, 
  Moon,
  Sun,
  FileSpreadsheet,
  ArrowLeft
} from 'lucide-react';

const Header = ({ 
  model, 
  onImport, 
  onExport, 
  onSave,
  onBack,
  onToggleTheme,
  isDarkMode,
  onTest,
  showThemeToggle
}: { 
  model: DmnModel | null, 
  onImport: () => void, 
  onExport: () => void,
  onSave?: () => void,
  onBack?: () => void,
  onToggleTheme: () => void,
  isDarkMode: boolean,
  onTest: () => void,
  showThemeToggle: boolean
}) => (
  <header className="h-16 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between px-4 sticky top-0 z-30 transition-colors">
    <div className="flex items-center gap-4">
      <Button variant="ghost" size="icon" onClick={onBack} className="text-slate-400 hover:text-slate-700 dark:text-slate-500 dark:hover:text-slate-300">
        <ArrowLeft size={20} />
      </Button>
      <div className="flex items-center gap-2 text-primary hover:text-emerald-700 cursor-pointer transition-colors">
        <div className="bg-primary/10 p-2 rounded-lg">
            <FileSpreadsheet size={20} className="text-primary" />
        </div>
        <h1 className="text-lg font-bold tracking-tight text-slate-800 dark:text-slate-100">Peak DMN</h1>
      </div>
      
      <div className="h-8 w-px bg-slate-200 dark:bg-slate-700 mx-2"></div>
      
      <span className="text-sm font-medium text-slate-700 dark:text-slate-400">{model?.name || 'Loading...'}</span>
    </div>

    <div className="flex items-center gap-2">
      <div className="flex items-center bg-slate-50 dark:bg-slate-800/50 rounded-md border border-slate-200 dark:border-slate-700 p-1 mr-4">
        <button 
            onClick={onTest}
            disabled={!model}
            className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-slate-800 text-emerald-700 dark:text-emerald-400 rounded shadow-sm text-xs font-bold uppercase tracking-wide border border-slate-100 dark:border-slate-700 hover:border-emerald-200 dark:hover:border-emerald-500/50 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Play size={14} fill="currentColor" />
          Test
        </button>
      </div>

      <div className="flex items-center gap-1 mr-4">
         {showThemeToggle && (
           <Button variant="ghost" size="icon" title="Toggle Theme" onClick={onToggleTheme}>
              {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
           </Button>
         )}
         <Button variant="ghost" className="hidden lg:flex" onClick={onImport}>
            <Upload size={16} /> Import
         </Button>
         <Button variant="ghost" className="hidden lg:flex" onClick={onExport} disabled={!model}>
            <Download size={16} /> Export
         </Button>
      </div>

      <div className="flex items-center gap-2">
         <Button variant="primary" className="shadow-emerald-200" disabled={!model} onClick={onSave}>
            <Save size={16} /> Save
         </Button>
      </div>
    </div>
  </header>
);

interface EditorProps {
    initialXml?: string;
    onSave?: (xml: string) => void;
    onBack?: () => void;
    theme?: 'light' | 'dark';
}

export const Editor = (props: EditorProps) => {
  const [model, setModel] = useState<DmnModel | null>(null);
  const [isTestOpen, setTestOpen] = useState(false);
  
  // Theme State
  // Priority: Prop > Global window fallback > Internal state
  const [internalIsDarkMode, setInternalIsDarkMode] = useState(false);
  const effectiveTheme = props.theme || (window as any).DMN_EDITOR_THEME;
  const isDarkMode = effectiveTheme !== undefined ? effectiveTheme === 'dark' : internalIsDarkMode;

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Initialize with XML
  useEffect(() => {
    try {
        // Safe access to props.initialXml avoids ReferenceError in some environments
        const xmlToLoad = props.initialXml || (window as any).initialXml || INITIAL_DMN_XML;
        const parsed = parseDmnXml(xmlToLoad);
        setModel(parsed);
    } catch (e) {
        console.error("Failed to parse initial DMN XML", e);
    }
  }, [props.initialXml]);

  // Sync theme with document class
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  const handleSave = () => {
    if (!model) return;
    const xmlStr = generateDmnXml(model);
    if (props.onSave) {
        props.onSave(xmlStr);
    } else if ((window as any).onDmnSave) {
        (window as any).onDmnSave(xmlStr);
    } else {
        console.log("Model saved internally:", xmlStr);
    }
  };

  const handleExport = () => {
    if (!model) return;
    const xmlStr = generateDmnXml(model);
    const blob = new Blob([xmlStr], { type: 'text/xml' });
    const url = URL.createObjectURL(blob);
    
    const downloadAnchorNode = document.createElement('a');
    downloadAnchorNode.setAttribute("href", url);
    downloadAnchorNode.setAttribute("download", `${model.name.replace(/\s+/g, '_')}.dmn`);
    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
    URL.revokeObjectURL(url);
  };

  const handleImportClick = () => {
      fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (event) => {
          try {
              const xml = event.target?.result as string;
              const parsed = parseDmnXml(xml);
              setModel(parsed);
          } catch (err) {
              console.error(err);
              alert('Failed to parse DMN file. Please ensure it is a valid DMN 1.3+ XML.');
          }
      };
      reader.readAsText(file);
      e.target.value = '';
  };

  const toggleTheme = () => {
      if (props.theme === undefined) {
          setInternalIsDarkMode(!internalIsDarkMode);
      }
  };

  if (!model) return <div className="h-screen w-full flex items-center justify-center bg-slate-50 dark:bg-slate-950 text-slate-500">Loading Editor...</div>;

  return (
    <div className="flex flex-col h-screen bg-slate-50 dark:bg-slate-950 font-sans text-slate-900 dark:text-slate-200 transition-colors">
      <input 
        type="file" 
        ref={fileInputRef} 
        onChange={handleFileChange} 
        className="hidden" 
        accept=".dmn,.xml"
      />

      <Header 
        model={model} 
        onImport={handleImportClick}
        onExport={handleExport}
        onSave={handleSave}
        onBack={props.onBack || (window as any).onDmnBack}
        onToggleTheme={toggleTheme}
        isDarkMode={isDarkMode}
        onTest={() => setTestOpen(true)}
        showThemeToggle={props.theme === undefined}
      />
      
      <main className="flex-1 flex flex-col relative overflow-hidden">
        <DecisionTable 
          model={model} 
          onModelChange={setModel} 
        />
        
        <div className="h-8 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between px-4 text-xs text-slate-500 dark:text-slate-400 shrink-0 transition-colors">
          <div className="flex items-center gap-4">
            <span>Rules: {model.rules.length}</span>
            <span>Columns: {model.columns.length}</span>
            <span className="text-emerald-600 dark:text-emerald-500 font-medium">{model.hitPolicy} Policy</span>
          </div>
          <div>
            Last edited: Just now
          </div>
        </div>
      </main>

      <TestModal 
        isOpen={isTestOpen} 
        onClose={() => setTestOpen(false)} 
        model={model}
      />
    </div>
  );
}